/**
 * Station error detail catalogue: error code -> errDetail bit -> translation keys.
 *
 * Copied from the BE error detail content (the source Diagnose uses). The BE
 * keys 3000 details by bitmask value (1, 2, 4 ... 2048, and 192 for bits 6 + 7);
 * here they are keyed by bit number, as returned by findErrorBit.
 *
 * Text lives in Tolgee (project 6). Keys are written out in full so
 * `tolgee extract` can find them.
 */

export interface StationErrorDetailKeys {
  titleKey: string
  reasonKey: string
  actionKeys: string[]
}

interface StationErrorDetailEntry {
  default: StationErrorDetailKeys
  bits?: Record<number, StationErrorDetailKeys>
}

const SOLENOID_COIL_ACTIONS = [
  'stationErrorDetail_action_oneCoilPerDecoder',
  'stationErrorDetail_action_replaceCoil',
  'stationErrorDetail_action_testCoilRange',
]

const NETWORK_ERROR: StationErrorDetailEntry = {
  default: {
    titleKey: 'stationErrorDetail_network_title',
    reasonKey: 'stationErrorDetail_network_reason',
    actionKeys: [
      'stationErrorDetail_action_checkInternet',
      'stationErrorDetail_action_checkControllerInternet',
    ],
  },
}

const DECODER_NO_RESPONSE: StationErrorDetailEntry = {
  default: {
    titleKey: 'stationErrorDetail_1000_title',
    reasonKey: 'stationErrorDetail_1000_reason',
    actionKeys: [
      'stationErrorDetail_action_checkDecoderPresence',
      'stationErrorDetail_action_checkWireResistance',
    ],
  },
}

export const STATION_ERROR_DETAILS: Record<number, StationErrorDetailEntry> = {
  400: NETWORK_ERROR,
  401: NETWORK_ERROR,
  403: NETWORK_ERROR,
  404: NETWORK_ERROR,
  408: NETWORK_ERROR,
  500: NETWORK_ERROR,
  1000: DECODER_NO_RESPONSE,
  1001: DECODER_NO_RESPONSE,
  1002: DECODER_NO_RESPONSE,
  1003: {
    default: {
      titleKey: 'stationErrorDetail_1003_title',
      reasonKey: 'stationErrorDetail_1003_reason',
      actionKeys: ['stationErrorDetail_action_turnOffDecoders'],
    },
  },
  3000: {
    default: {
      titleKey: 'stationErrorDetail_3000_title',
      reasonKey: 'stationErrorDetail_3000_reason',
      actionKeys: ['stationErrorDetail_action_check2WireDecoderSolenoid'],
    },
    bits: {
      0: {
        titleKey: 'stationErrorDetail_3000_bit0_title',
        reasonKey: 'stationErrorDetail_3000_bit0_reason',
        actionKeys: ['stationErrorDetail_action_retryCommand'],
      },
      1: {
        titleKey: 'stationErrorDetail_3000_bit1_title',
        reasonKey: 'stationErrorDetail_3000_bit1_reason',
        actionKeys: SOLENOID_COIL_ACTIONS,
      },
      2: {
        titleKey: 'stationErrorDetail_3000_bit2_title',
        reasonKey: 'stationErrorDetail_3000_bit2_reason',
        actionKeys: [
          'stationErrorDetail_action_reduceActiveStations',
          'stationErrorDetail_action_runTwoWireDiagnostic',
        ],
      },
      3: {
        titleKey: 'stationErrorDetail_3000_bit3_title',
        reasonKey: 'stationErrorDetail_3000_bit3_reason',
        actionKeys: SOLENOID_COIL_ACTIONS,
      },
      4: {
        titleKey: 'stationErrorDetail_3000_bit4_title',
        reasonKey: 'stationErrorDetail_3000_bit4_reason',
        actionKeys: SOLENOID_COIL_ACTIONS,
      },
      5: {
        titleKey: 'stationErrorDetail_3000_bit5_title',
        reasonKey: 'stationErrorDetail_3000_bit5_reason',
        actionKeys: ['stationErrorDetail_action_checkSplices'],
      },
      6: {
        titleKey: 'stationErrorDetail_3000_bit6_title',
        reasonKey: 'stationErrorDetail_3000_bit6_reason',
        actionKeys: SOLENOID_COIL_ACTIONS,
      },
      7: {
        titleKey: 'stationErrorDetail_3000_bit7_title',
        reasonKey: 'stationErrorDetail_3000_bit7_reason',
        actionKeys: [
          'stationErrorDetail_action_inspectPlunger',
          'stationErrorDetail_action_contactManufacturer',
        ],
      },
      8: {
        titleKey: 'stationErrorDetail_3000_bit8_title',
        reasonKey: 'stationErrorDetail_3000_bit8_reason',
        actionKeys: SOLENOID_COIL_ACTIONS,
      },
      9: {
        titleKey: 'stationErrorDetail_3000_bit9_title',
        reasonKey: 'stationErrorDetail_3000_bit9_reason',
        actionKeys: SOLENOID_COIL_ACTIONS,
      },
      10: {
        titleKey: 'stationErrorDetail_3000_bit10_title',
        reasonKey: 'stationErrorDetail_3000_bit10_reason',
        actionKeys: SOLENOID_COIL_ACTIONS,
      },
      11: {
        titleKey: 'stationErrorDetail_3000_bit11_title',
        reasonKey: 'stationErrorDetail_3000_bit11_reason',
        actionKeys: SOLENOID_COIL_ACTIONS,
      },
      67: {
        titleKey: 'stationErrorDetail_3000_bit67_title',
        reasonKey: 'stationErrorDetail_3000_bit67_reason',
        actionKeys: [
          'stationErrorDetail_action_oneCoilPerIo',
          'stationErrorDetail_action_checkValve',
        ],
      },
    },
  },
  3001: {
    default: {
      titleKey: 'stationErrorDetail_3001_title',
      reasonKey: 'stationErrorDetail_3001_reason',
      actionKeys: ['stationErrorDetail_action_retryCommand'],
    },
  },
}
